import { defineStore } from 'pinia'

import {
    getCountries, getLanguages, getMaintenanceMode, getTimezones,
} from '@/api/app-routes'

import { useDefaultStore } from '@/store/defaultStore'

import { changeTheme, currentTheme, setTheme } from '@/helpers/theme-local-storage-helper'

import { MAINTENANCE_MODE_STATUS } from '@/constants/maintenanceMode'
import { THEMES } from '@/constants/themes'
import { AGENT_VIEWS } from '@/constants/agentViews'

export const useAppStore = defineStore('app', {
    state: () => ({
        timezones: [],
        countries: [],
        languages: [],
        maintenanceMode: MAINTENANCE_MODE_STATUS.disable,
        themeTimerId: null,
        theme: currentTheme(),
        routeIsLoading: false,

        aiAgentOpen: false,
        currentAiAgentView: AGENT_VIEWS.sidebar,
    }),

    getters: {
        aiAgentOnWindow(state) {
            return state.currentAiAgentView === AGENT_VIEWS.window
        },
    },

    actions: {
        toggleAiAgent() {
            this.aiAgentOpen = !this.aiAgentOpen
        },

        toggleAiAgentView(view) {
            this.currentAiAgentView = view
        },

        setRouteLoading(loading) {
            this.routeIsLoading = loading
        },

        applyTheme(id) {
            changeTheme(id)

            this.theme = id

            if (id !== THEMES.auto.id) {
                clearTimeout(this.themeTimerId)
            } else {
                this.themeTimerId = setTheme()
            }
        },

        setMaintenanceMode({ maintenance_mode }) {
            this.maintenanceMode = maintenance_mode
        },

        changeTimerId(id) {
            this.themeTimerId = id
        },

        getTimezoneNameById(id) {
            const timezone = this.timezones.find((timezone) => timezone.id === id)

            if (!!timezone && timezone.hasOwnProperty('display')) {
                return timezone.display
            }

            return ''
        },

        getTimezoneOffsetById(id) {
            const timezone = this.timezones.find((timezone) => timezone.id === id)

            return timezone ? timezone.php : null
        },

        getCountryNameById(id) {
            const country = this.countries.find((country) => country.id === id)

            if (!!country && country.hasOwnProperty('name')) {
                return country.name
            }

            return ''
        },

        getCountryCodeById(id) {
            const country = this.countries.find((country) => country.id === id)

            if (!!country && country.hasOwnProperty('code')) {
                return country.code
            }

            return ''
        },

        getCountryPrefixById(id) {
            const country = this.countries.find((country) => country.id === id)

            if (!!country && country.hasOwnProperty('num_code')) {
                return country.num_code
            }

            return ''
        },

        getCountryById(id) {
            return this.countries.find((country) => country.id === id)
        },

        getCountriesWithCode() {
            return this.countries.map((country) => ({
                ...country,
                name: `${country.name} (${country.num_code})`,
            }))
        },

        async goToLoadMaintenanceMode() {
            const { success, payload, message } = await getMaintenanceMode()

            if (success) {
                this.setMaintenanceMode(payload)
            } else {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }
        },

        async loadLanguages() {
            if (Array.isArray(this.languages) && this.languages.length) {
                return
            }

            const { success, payload, message } = await getLanguages()

            if (success) {
                this.languages = payload
            } else {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }
        },

        async loadTimezones() {
            if (Array.isArray(this.timezones) && this.timezones.length) {
                return
            }

            const { success, payload, message } = await getTimezones()

            if (success) {
                this.timezones = payload
            } else {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }
        },

        async loadCountries() {
            if (Array.isArray(this.countries) && this.countries.length) {
                return
            }

            const { success, payload, message } = await getCountries({ sort: 'byPopularity' })

            if (success) {
                this.countries = payload
            } else {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }
        },
    },
})
